import { Injectable } from '@angular/core';
import { QuestionnairesService } from './questionnaires.service';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { IUser } from 'src/app/core/models/user.model';
import { QuestionariType, QuestionnaireResponse } from '../model/questionnaire-response';
import { Errors, ErrorType } from 'src/app/shared/enums/error';
import { PsychologyAssessment } from '../medical-record-page/pages/psychology/model/psychology-assessment.model';
import { PsychologyBackground } from '../medical-record-page/pages/psychology/model/psychology-background.model';
import { PsychologyEvolution } from '../medical-record-page/pages/psychology/model/psychology-evolution.model';
import { PsychologyDistress } from '../medical-record-page/pages/psychology/model/psychology-distress.model';
import { getAuthStateSelect } from 'src/app/core/store/auth/auth.selector';
import { AuthState } from 'src/app/core/store/auth/auth.state';
import { Store } from '@ngrx/store';
import { PatientService } from 'src/app/shared/services/patient/patient-info.service';

export type Psychology = PsychologyAssessment | PsychologyBackground | PsychologyEvolution | PsychologyDistress;

@Injectable({
  providedIn: 'root'
})
export class PsychologyService {

  private patientSelectedId: string | null = null;
  private psychologySubject = new BehaviorSubject<any>(null);

  subscription!: Subscription;
  userLogged: IUser | null = null;

  constructor(
    private questionnairesService: QuestionnairesService,
    private store: Store<AuthState>,
    private patientInfoService: PatientService,
  ) {
    this.psychologySubject.next(null);

    this.subscription = (
      combineLatest([
        this.store.select(getAuthStateSelect),
        this.patientInfoService.getPatientId()
      ])
        .subscribe(([auth, patientId]) => {
          if (!auth.user || !patientId) return;

          this.userLogged = auth.user;
          this.patientSelectedId = patientId;
        }));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  get getPsychology() {
    return this.psychologySubject.asObservable();
  }

  async loadPsychology(patientId: string, user: IUser): Promise<void> {

    this.psychologySubject.next(null);
    await this.questionnairesService.getQuestionnaires(
      'psychology', patientId, user.practitionerId)
      .then(result => {
        if (result) {
          this.psychologySubject.next({
            ...result,
            history: [...(result?.history ?? [])],
            myclinicHistory: [...(result?.myclinicHistory ?? [])],
            todayMulti: { ...result?.todayMulti }
          });
        }
      });
  }

  async savePsychology({ item, name }: QuestionariType, patientId: string, userLogged: IUser): Promise<void> {
    try {
      await this.questionnairesService.saveNewQuestionnaireDraft({ item, name })
        .then(() => {
          this.loadPsychology(patientId, userLogged);
        });

    } catch (error: any) {
      if (error instanceof Errors) throw error;

      throw new Errors(
        ErrorType.SERVICE_ERROR,
        'Erro ao salvar questionário de psicologia.',
        error
      );
    }
  }

  async updatePsychology({ item, name }: QuestionariType, patientId: string, userLogged: IUser): Promise<void> {
    try {
      await this.questionnairesService.updateQuestionnaireDraft({ item, name })
        .then(() => {
          this.loadPsychology(patientId, userLogged);
        });

    } catch (error: any) {
      if (error instanceof Errors) throw error;

      throw new Errors(
        ErrorType.SERVICE_ERROR,
        'Erro ao atualizar questionário de psicologia.',
        error
      );
    }
  }
}
